/**
 * "Not Interested" — the negative half of the affinity signal.
 *
 * Every ranking input the site had was positive: a view, a hover, a wishlist, a
 * purchase. A reader with no way to say "less of this" could only push a showcase
 * down by engaging with something else, which is a slow and indirect way to
 * express a thing they already know. This module is the shared contract for
 * saying it directly.
 *
 * WHAT A NEGATIVE MEANS HERE
 * --------------------------
 * Deprioritising, not banning. A dismissed category or tag keeps its place in the
 * catalogue and can be surfaced again — it simply has to earn it, which is what
 * `NEGATIVE_WEIGHT` and the "count interest accrued since the dismissal" rule in
 * `utils/recommendations.ts` implement between them. Someone who dismisses
 * "Camping" once after a single bad row should not be locked out of camping gear
 * for the rest of the year, and someone who dismisses it repeatedly should stop
 * seeing it without anyone having to model that separately.
 *
 * WHY THE TARGET IS A UNION
 * -------------------------
 * Showcases are built on two different axes. A category row is "Guitars"; a tag
 * row is "vintage". The affinity tables behind them are separate
 * (`user_category_interests_mv` / `user_tag_interests_mv`), so a dismissal has to
 * name which one it applies to or it lands on neither — which is what happened
 * before this existed: the For You page's category rows had a dismiss control and
 * the tag rows had nothing, so half the surface produced no negative at all.
 */
import { isPlainRecord, readJSON, writeJSON } from './guarded-storage';

/**
 * How hard a "not interested" is.
 *
 * This used to be a six-item list of REASONS -- not relevant, too expensive,
 * poor quality, wrong category, already purchased, other. Six answers to a
 * question nobody came to the page to answer, and none of them changed what
 * happened next: every one produced the same discount, so the menu was asking
 * for market research in the middle of an action.
 *
 * Two answers, and they differ in what they DO. That is the whole justification
 * for a menu here rather than a single click: "less" is the soft signal ranking
 * discounts, "none" is the reader telling us to stop, and a product that offers
 * only the soft one eventually gets used as if it were the hard one.
 */
export const NOT_INTERESTED_OPTIONS = [
  {
    value: 'show_less',
    label: 'Show less',
    hint: 'Fewer rows like this',
  },
  {
    value: 'show_none',
    label: 'Show none',
    hint: 'Stop showing this entirely',
  },
] as const;

export type NotInterestedStrength = (typeof NOT_INTERESTED_OPTIONS)[number]['value'];

/**
 * Values the `reason` column has held, including the ones it no longer produces.
 *
 * Kept because production rows carry them and the ingest schema is shared with
 * clients that may not have reloaded yet -- narrowing the enum would 400 the
 * whole batch an old tab sends, losing every event in it, not just the stale
 * one. Read as `show_less`: they were all the same discount.
 */
export const LEGACY_NOT_INTERESTED_REASONS = [
  'not_relevant',
  'too_expensive',
  'poor_quality',
  'wrong_category',
  'already_purchased',
  'other',
] as const;

export type NotInterestedReason =
  | NotInterestedStrength
  | (typeof LEGACY_NOT_INTERESTED_REASONS)[number];

/** Whether a stored row asked for exclusion rather than a discount. */
export function isExclusion(reason: string | null | undefined): boolean {
  return reason === 'show_none';
}

/** Where the dismissal happened, for reading the signal back by surface. */
export type NotInterestedSource =
  | 'search'
  | 'browse'
  | 'recommendations'
  | 'similar'
  | 'for_you'
  | 'home'
  | 'product';

/**
 * What a showcase is *about*, and therefore what a dismissal of it means.
 *
 * `label` is only ever shown to the reader ("We'll show you less like ..."); the
 * axis fields are what reach the analytics row and the ranking queries.
 */
export type NotInterestedTarget =
  | {
      kind: 'category';
      label: string;
      /**
       * The category, when the row is one. Rows on the home page are built from
       * a category GROUP instead and carry only the id below -- both are the
       * same axis to a reader, and keeping them one kind is what stops a caller
       * having to know which shape a given showcase happens to be.
       */
      categoryId?: number;
      categoryGroupId?: number;
    }
  | {
      kind: 'tag';
      label: string;
      tag: string;
      /** Present when the tag row was built inside a known category. */
      categoryId?: number;
    };

/**
 * A stable key for one target, used both as the storage key and as the identity
 * an undo has to match. Tags are lower-cased because the tag vocabulary is not:
 * 1,340 of 2,233 approved tags carry a non-lowercase name, and 'Vintage' and
 * 'vintage' are the same preference to everyone except a string comparison.
 */
export function targetKey(target: NotInterestedTarget): string {
  if (target.kind === 'tag') return `tag:${target.tag.trim().toLowerCase()}`;
  return target.categoryId !== undefined
    ? `category:${target.categoryId}`
    : `group:${target.categoryGroupId}`;
}

/**
 * The analytics payload for one dismissal.
 *
 * `listing_id` is 0 for a showcase. The column is not nullable and the event was
 * designed around a single listing, but a showcase dismissal is about the whole
 * row — sending a representative listing id would make the strongest negative
 * signal on the site look like an opinion about one item, and every per-listing
 * report built on this event would then be wrong.
 */
export interface NotInterestedSignal {
  event_type: 'not_interested';
  listing_id: number;
  listing_title: string;
  category_id?: number;
  category_group_id?: number;
  tags?: string[];
  reason?: NotInterestedReason;
  source_page: NotInterestedSource;
  /** 'undo' when the reader took it back; absent otherwise. */
  action?: 'undo';
}

/**
 * Which axis a stored dismissal applies to, read back off the row.
 *
 * There is deliberately no `showcase_kind` column. Every field on this payload
 * is spread straight into a `JSONEachRow` insert, and ClickHouse rejects a field
 * that is not a column -- with `input_format_skip_unknown_fields` unset, a new
 * name here would not be quietly dropped, it would fail the insert for every
 * `not_interested` event and park the rows in the outbox. The kind is already
 * unambiguous from what IS on the row, so the column would buy nothing but a
 * migration that has to land in three environments before the code can ship.
 */
export function signalKind(row: {
  tags?: string[] | null;
  category_id?: number | null;
  category_group_id?: number | null;
}): NotInterestedTarget['kind'] | null {
  if (row.tags && row.tags.length > 0) return 'tag';
  if (row.category_id || row.category_group_id) return 'category';
  return null;
}

/**
 * Build the event for a dismissal, or for taking one back.
 *
 * `strength` rides in the existing `reason` column rather than a new one. Every
 * field here is spread into a JSONEachRow insert and ClickHouse rejects the ROW
 * for an unknown name, so a dedicated column would mean a migration in three
 * environments before any of this could ship -- and `reason` already holds
 * exactly this: what the reader said when they dismissed it.
 */
export function buildNotInterestedSignal(params: {
  target: NotInterestedTarget;
  source: NotInterestedSource;
  strength?: NotInterestedStrength;
  undo?: boolean;
}): NotInterestedSignal {
  const { target, source, strength, undo } = params;

  const signal: NotInterestedSignal = {
    event_type: 'not_interested',
    listing_id: 0,
    listing_title: target.label,
    source_page: source,
  };

  if (target.kind === 'category') {
    if (target.categoryId !== undefined) signal.category_id = target.categoryId;
    if (target.categoryGroupId !== undefined) {
      signal.category_group_id = target.categoryGroupId;
    }
  } else {
    // Lower-cased to match `user_tag_interests_mv`, which groups on `lower(tag)`.
    // An unnormalised tag here would be a negative nothing ever reads.
    signal.tags = [target.tag.trim().toLowerCase()];
    if (target.categoryId !== undefined) signal.category_id = target.categoryId;
  }

  // Only set when true. The ranking queries filter on `action != 'undo'`, and an
  // explicit empty string is not the same as the column default there.
  if (undo) signal.action = 'undo';
  if (strength) signal.reason = strength;

  return signal;
}

/**
 * Remembered dismissals, per browser.
 *
 * Two keys rather than one because `dismissedCategories` already exists and
 * already holds entries for real readers; folding it into a combined store would
 * silently forget every dismissal made before this shipped. Storage is a
 * convenience, not the record — the record is the analytics event — so a blocked
 * or cleared store simply shows the row again, which is the right degradation.
 */
const STORAGE_KEYS = {
  category: 'dismissedCategories',
  tag: 'dismissedTags',
} as const;

export interface DismissalEntry {
  /** Absent on entries written before the two-option menu; read as 'show_less'. */
  strength?: NotInterestedStrength;
  dismissedAt: string;
}

type DismissalStore = Record<string, unknown>;

const readStore = (kind: NotInterestedTarget['kind']): DismissalStore =>
  readJSON<DismissalStore>('local', STORAGE_KEYS[kind], {}, isPlainRecord);

/**
 * The id a dismissal is filed under within its own store.
 *
 * Deliberately NOT `targetKey`: the category store predates this module and is
 * keyed by bare category id. Prefixing would orphan every entry already there.
 */
function storageId(target: NotInterestedTarget): string {
  if (target.kind === 'tag') return target.tag.trim().toLowerCase();
  // A group is prefixed and a category is not, so a group id can never be
  // mistaken for a category id that happens to share the number -- and every
  // entry written before groups existed still reads back as the category it was.
  return target.categoryId !== undefined
    ? String(target.categoryId)
    : `g${target.categoryGroupId}`;
}

export function isDismissed(target: NotInterestedTarget): boolean {
  return storageId(target) in readStore(target.kind);
}

/** Every dismissed id of one kind, for filtering a page's rows in one pass. */
export function readDismissedIds(kind: NotInterestedTarget['kind']): Set<string> {
  return new Set(Object.keys(readStore(kind)));
}

export function rememberDismissal(
  target: NotInterestedTarget,
  strength?: NotInterestedStrength,
): void {
  const store = readStore(target.kind);
  const entry: DismissalEntry = { dismissedAt: new Date().toISOString() };
  if (strength) entry.strength = strength;
  store[storageId(target)] = entry;
  writeJSON('local', STORAGE_KEYS[target.kind], store);
}

export function forgetDismissal(target: NotInterestedTarget): void {
  const store = readStore(target.kind);
  delete store[storageId(target)];
  writeJSON('local', STORAGE_KEYS[target.kind], store);
}

/**
 * How much "Show less" discounts the interest already accumulated for a target.
 *
 * A multiplier rather than a filter, and that is the whole difference between
 * deprioritising and banning: at 0.25 a dismissed tag with four times the
 * interest of its neighbour still outranks it, while one that was marginal to
 * begin with drops off the page. Each further "Show less" multiplies again, so
 * saying it twice means it.
 *
 * "Show none" does NOT use this. A reader who asks for none and gets a quarter
 * as many has been told no; that path excludes the target outright, which is why
 * it is a separate answer in the menu rather than a harder press of the same one.
 */
export const NEGATIVE_WEIGHT = 0.25;
